import dayjs from 'dayjs';
import _ from 'lodash';

export const formatDate = (timestamp) => {
  if (!timestamp) return '';
  return dayjs(String(timestamp)).format('D/MM/YYYY');
};

export const formatDateHour = (timestamp) => {
  if (!timestamp) return '';
  return dayjs(String(timestamp)).format('D/MM/YYYY H:mm:ss');
};

export const formatTime = (timestamp) => {
  if (!timestamp) return '';
  return dayjs(String(timestamp)).format('HH:mm:ss');
};

export const formatMinutes = (timestamp) => {
  if (!timestamp) return '';
  return dayjs(String(timestamp)).format('mm');
};

export const formatHoursAndMinutes = (timestamp) => {
  if (!timestamp) return '';
  return dayjs(String(timestamp)).format('HH:mm');
};

export const timeStringToDate = (timeString, format = 'HH:mm:ss') => {
  const time = dayjs(timeString, format);
  const now = dayjs();
  return now.hour(time.hour()).minute(time.minute()).second(time.second()).millisecond(0).toDate();
};

/**
 * Mapeo de estrategias de manipulación de hora para fechas, usado por `localDateToPlainText`.
 *
 * Cada propiedad corresponde a un "journey" y define cómo se debe modificar la hora
 * de un objeto Dayjs:
 *
 * - 'start': fuerza la hora al inicio del día (00:00:00.000)
 * - 'end': fuerza la hora al final del día (23:59:59.000)
 * - 'now': inyecta la hora actual del sistema local
 *
 * Este objeto implementa un patrón tipo enum/negator: solo se aplica la función si existe
 * una clave correspondiente al valor de `journey`.
 *
 * @type {Object.<string, function(dayjs.Dayjs): dayjs.Dayjs>}
 * @example
 * const date = dayjs('2026-01-15T14:30:45');
 * const startOfDay = journeyHandlers.start(date); // 2026-01-15T00:00:00
 * const endOfDay = journeyHandlers.end(date);     // 2026-01-15T23:59:59
 * const nowTime = journeyHandlers.now(date);      // 2026-01-15T<hora actual>
 */
export const journeyHandlers = {
  start: (d) => d.hour(0).minute(0).second(0).millisecond(0),
  end: (d) => d.hour(23).minute(59).second(59).millisecond(0),
  now: (d) => {
    const now = dayjs();
    return d.hour(now.hour()).minute(now.minute()).second(now.second()).millisecond(0);
  },
};

/**
 * Convierte una fecha local en texto formateado.
 *
 * @param {string|Date|dayjs.Dayjs} date
 * @param {Object} [options]
 * @param {'start'|'end'|'now'} [options.journey]
 *        - 'start': fuerza hora 00:00:00
 *        - 'end': fuerza hora 23:59:59
 *        - 'now': inyecta la hora actual local
 *        - undefined: mantiene la hora original de date
 * @param {boolean} [options.useUTC=false] usar UTC
 * @param {string} [options.format='YYYY/MM/DDTHH:mm:ss'] formato de salida
 * @returns {string|null} fecha formateada o null si date inválido
 */
export const localDateToPlainText = (date, options = {}) => {
  if (!date) return null;

  const defaultOptions = {
    journey: undefined,
    useUTC: false,
    format: 'YYYY-MM-DD HH:mm:ss',
  };

  const finalOptions = _.merge({}, defaultOptions, options);

  let baseDate = finalOptions.useUTC ? dayjs.utc(date) : dayjs(date);

  if (!baseDate.isValid()) return null;

  if (journeyHandlers[finalOptions.journey]) {
    baseDate = journeyHandlers[finalOptions.journey](baseDate);
  }

  return baseDate.format(finalOptions.format);
};

/**
 * Convierte un string de fecha a un objeto dayjs válido.
 *
 * @param {string} dateString  Fecha en formato texto
 * @param {Object} [options]
 * @param {string} [options.format='YYYY/MM/DDTHH:mm:ss'] Formato esperado
 * @param {boolean} [options.useUTC=false] Parsear en UTC
 * @param {boolean} [options.strict=true] Validación estricta del formato
 * @returns {dayjs.Dayjs|null} Objeto dayjs válido o null si es inválido
 */
export const plainTextToLocalDate = (dateString, options = {}) => {
  if (!dateString) return null;

  const defaultOptions = {
    format: 'YYYY-MM-DD HH:mm:ss',
    useUTC: false,
    strict: true,
    nativeDate: false,
  };

  const finalOptions = _.merge({}, defaultOptions, options);

  const parsed = finalOptions.useUTC
    ? dayjs.utc(dateString, finalOptions.format, finalOptions.strict)
    : dayjs(dateString, finalOptions.format, finalOptions.strict);

  const result = parsed.isValid() ? parsed : null;

  if (finalOptions.nativeDate) {
    return result?.toDate();
  }

  return result;
};

export const isDaysInstance = (value) => {
  return dayjs.isDayjs(value);
};

export const generateCurrentDates = () => {
  const date = dayjs().locale('en');
  const dateTime = date.toDate();
  const dateTimeString = localDateToPlainText(_.clone(dateTime));
  const dateString = localDateToPlainText(_.clone(dateTime), {
    format: 'YYYY-MM-DD',
  });
  const timeString = localDateToPlainText(_.clone(dateTime), { format: 'HH:mm:ss' });
  const dateUTC = dateTime.toISOString();
  return {
    date,
    dateTime,
    dateTimeString,
    dateString,
    timeString,
    dateUTC,
  };
};
